import { Random } from '../utils/Random.js';

/**
 * Layered procedural ambience: digital hum, distant city, electronic chatter.
 */
export class AmbientAudio {
  /**
   * @param {AudioContext} ctx
   * @param {GainNode} bus
   * @param {import('../config.js').CONFIG['audio']} config
   */
  constructor(ctx, bus, config) {
    this._ctx = ctx;
    this._bus = bus;
    this._config = config.ambient;
    this._rng = new Random(config.seed);
    this._movementMix = 0;
    this._started = false;

    /** @type {OscillatorNode[]} */
    this._oscillators = [];
    /** @type {GainNode[]} */
    this._layers = [];
    /** @type {AudioBufferSourceNode | null} */
    this._cityNoise = null;
    /** @type {GainNode | null} */
    this._cityGain = null;
    /** @type {number} */
    this._electronicTimer = 0;
  }

  start() {
    if (this._started) return;
    this._started = true;
    this._buildHum();
    this._buildCityBed();
    this._buildElectronicLayer();
  }

  /**
   * @param {number} delta
   * @param {{
   *   horizontalSpeed?: number;
   *   sprinting?: boolean;
   *   onGround?: boolean;
   *   inSimulation?: boolean;
   * }} state
   */
  update(delta, state) {
    const speed = state.horizontalSpeed ?? 0;
    const target =
      state.inSimulation && state.onGround
        ? Math.min(1, speed / (state.sprinting ? 9 : 5))
        : 0;
    this._movementMix += (target - this._movementMix) * Math.min(1, delta * 4);

    if (this._cityGain) {
      const base = this._config.cityGain;
      this._cityGain.gain.value =
        base * (0.85 + this._movementMix * 0.25);
    }

    for (let i = 0; i < this._layers.length; i += 1) {
      const layer = this._layers[i];
      if (i === 0) {
        layer.gain.value =
          this._config.humGain * (1 + this._movementMix * 0.08);
      }
    }

    this._electronicTimer -= delta;
    if (this._electronicTimer <= 0) {
      this._electronicTimer = this._rng.range(
        this._config.electronicIntervalMin,
        this._config.electronicIntervalMax,
      );
      this._pingElectronic();
    }
  }

  dispose() {
    for (const osc of this._oscillators) {
      try {
        osc.stop();
      } catch {
        /* already stopped */
      }
      osc.disconnect();
    }
    this._oscillators = [];
    this._layers = [];
    if (this._cityNoise) {
      try {
        this._cityNoise.stop();
      } catch {
        /* noop */
      }
      this._cityNoise.disconnect();
      this._cityNoise = null;
    }
    this._cityGain?.disconnect();
    this._cityGain = null;
    this._started = false;
  }

  _buildHum() {
    const freqs = this._config.humFrequencies;
    for (let i = 0; i < freqs.length; i += 1) {
      const osc = this._ctx.createOscillator();
      osc.type = i === 0 ? 'sine' : 'triangle';
      osc.frequency.value = freqs[i];

      const gain = this._ctx.createGain();
      gain.gain.value =
        this._config.humGain * (i === 0 ? 1 : 0.35 / (i + 1));

      const lfo = this._ctx.createOscillator();
      lfo.frequency.value = 0.07 + i * 0.03;
      const lfoGain = this._ctx.createGain();
      lfoGain.gain.value = 2 + i;
      lfo.connect(lfoGain);
      lfoGain.connect(osc.detune);

      osc.connect(gain);
      gain.connect(this._bus);
      osc.start();
      lfo.start();

      this._oscillators.push(osc, lfo);
      this._layers.push(gain);
    }
  }

  _buildCityBed() {
    const length = this._ctx.sampleRate * 2;
    const buffer = this._ctx.createBuffer(1, length, this._ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * 0.35;
    }

    this._cityNoise = this._ctx.createBufferSource();
    this._cityNoise.buffer = buffer;
    this._cityNoise.loop = true;

    const filter = this._ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = this._config.cityFilterHz;
    filter.Q.value = 0.4;

    this._cityGain = this._ctx.createGain();
    this._cityGain.gain.value = this._config.cityGain;

    this._cityNoise.connect(filter);
    filter.connect(this._cityGain);
    this._cityGain.connect(this._bus);
    this._cityNoise.start();
  }

  _buildElectronicLayer() {
    this._electronicTimer = this._rng.range(0.5, 2.5);
  }

  _pingElectronic() {
    const osc = this._ctx.createOscillator();
    const env = this._ctx.createGain();
    osc.type = 'square';
    const base = this._rng.range(900, 2400);
    osc.frequency.value = base;

    const t0 = this._ctx.currentTime;
    const dur = this._rng.range(0.02, 0.06);
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.exponentialRampToValueAtTime(
      this._config.electronicGain,
      t0 + 0.003,
    );
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    osc.connect(env);
    env.connect(this._bus);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }
}
